/**
 * API Keys Settings Page
 *
 * Allows workspace members to create and revoke API keys for programmatic
 * access to the priorities.ai platform.
 *
 * - Keys are scoped to the current workspace.
 * - The raw key is shown exactly once (at creation). Only a prefix is stored.
 * - Scopes follow the pattern '<resource>:<action>' (e.g. 'items:read').
 *   An empty scopes array grants full access.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  KeyRound,
  Plus,
  Trash2,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Shield,
  Clock,
  Loader2,
  Bot,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Zap,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { CopyButton } from '../../../components/ds';

// ============================================================================
// Types
// ============================================================================

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  is_agent?: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  revoked_at: string | null;
  created_by: string | null;
}

interface NewKeyResult {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  key: string;
}

const AVAILABLE_SCOPES = [
  { value: 'items:read', label: 'Items — Read' },
  { value: 'items:write', label: 'Items — Write' },
  { value: 'sessions:read', label: 'Sessions — Read' },
  { value: 'sessions:write', label: 'Sessions — Write' },
  { value: 'tool_sessions:read', label: 'Tool Sessions — Read' },
  { value: 'tool_sessions:write', label: 'Tool Sessions — Write' },
  { value: 'cycles:read', label: 'Cycles — Read' },
  { value: 'cycles:write', label: 'Cycles — Write' },
  { value: 'catchball:read', label: 'Catchball — Read' },
  { value: 'catchball:write', label: 'Catchball — Write' },
  { value: 'workspace:read', label: 'Workspace — Read' },
  { value: 'workspace:write', label: 'Workspace — Write' },
  { value: 'reports:read', label: 'Reports — Read' },
  { value: 'reports:write', label: 'Reports — Write' },
  { value: 'mandate:read', label: 'Mandate — Read' },
  { value: 'mandate:write', label: 'Mandate — Write' },
  { value: 'webhooks:read', label: 'Webhooks — Read' },
  { value: 'webhooks:write', label: 'Webhooks — Write' },
  { value: 'tasks:read', label: 'Tasks — Read' },
  { value: 'tasks:write', label: 'Tasks — Write' },
  { value: 'notifications:read', label: 'Notifications — Read' },
  { value: 'notifications:write', label: 'Notifications — Write' },
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}


// ============================================================================
// New Key Dialog
// ============================================================================

interface CreateKeyDialogProps {
  onClose: () => void;
  onCreated: (result: NewKeyResult) => void;
  workspaceId: string;
}

function CreateKeyDialog({ onClose, onCreated, workspaceId }: CreateKeyDialogProps) {
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [fullAccess, setFullAccess] = useState(true);
  const [isAgent, setIsAgent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  // Agent keys are for external AI agents (Hermes, OpenClaw, MCP clients). The
  // API denies every authority transition for them regardless of scopes, so a
  // full-access agent key makes no sense — default to a judgment-lane scope set.
  const AGENT_DEFAULT_SCOPES = [
    'items:read', 'items:write',
    'sessions:read', 'cycles:read',
    'catchball:read', 'catchball:write',
    'reports:read', 'workspace:read',
  ];

  const toggleAgent = () => {
    setIsAgent((prev) => {
      const next = !prev;
      if (next) {
        setFullAccess(false);
        setSelectedScopes((s) => (s.length === 0 ? AGENT_DEFAULT_SCOPES : s));
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);

    try {
      // Generate key material in the browser — same algorithm as the API route.
      // The raw key is returned to the caller and shown once; only the hash is stored.
      const randomBytes = crypto.getRandomValues(new Uint8Array(32));
      const suffix = Array.from(randomBytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      const rawKey = `pk_live_${suffix}`;
      const keyPrefix = suffix.slice(0, 8);

      const encoded = new TextEncoder().encode(rawKey);
      const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
      const keyHash = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');

      const supabase = getSupabaseClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: dbErr } = await supabase
        .from('api_keys')
        .insert({
          workspace_id: workspaceId,
          name: name.trim(),
          key_hash: keyHash,
          key_prefix: keyPrefix,
          scopes: fullAccess ? [] : selectedScopes,
          is_agent: isAgent,
          created_by: user.id,
        })
        .select('id, name, key_prefix, scopes, is_agent, created_at')
        .single();

      if (dbErr || !data) throw new Error(dbErr?.message ?? 'Failed to create API key');

      onCreated({ ...data, key: rawKey });
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg border border-[var(--rule)] bg-[var(--bg-raised)] shadow-2xl">
        <div className="border-b border-[var(--rule)] px-6 py-4">
          <h2 className="text-lg font-semibold text-[var(--text-pri)]">Create API Key</h2>
          <p className="mt-0.5 text-sm text-[var(--text-ter)]">
            The raw key will be shown once — save it securely.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Key name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. CI/CD Pipeline, Jira Integration"
              className="w-full border border-[var(--rule)] px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
          </div>

          <div>
            <label className="flex items-start gap-2.5 cursor-pointer border border-[var(--rule)] px-3 py-2.5">
              <input
                type="checkbox"
                checked={isAgent}
                onChange={toggleAgent}
                className="mt-0.5 rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm">
                <span className="font-medium text-[var(--text-pri)]">Agent key</span>
                <span className="block text-xs text-[var(--text-ter)]">
                  For external AI agents (Hermes, OpenClaw, MCP clients). Can read and add
                  attributed input, but can never perform authority transitions — ratify a
                  priority list, publish a session, or accept a Catchball proposal — regardless
                  of scopes.
                </span>
              </span>
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-[var(--text-sec)]">Scopes</label>
              <button
                type="button"
                onClick={() => setFullAccess((v) => !v)}
                disabled={isAgent}
                className={`text-xs font-medium px-2 py-0.5 rounded-full transition-colors ${
                  fullAccess
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-[var(--hover-tint)] text-[var(--text-ter)] hover:bg-[var(--hover-tint)]'
                } ${isAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {fullAccess ? 'Full access' : 'Custom scopes'}
              </button>
            </div>

            {fullAccess ? (
              <p className="text-sm text-[var(--text-ter)] border border-dashed border-[var(--rule)] bg-[var(--surface)] px-3 py-2">
                This key will have full access to all resources in the workspace.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 border border-[var(--rule)] p-3 max-h-48 overflow-y-auto">
                {AVAILABLE_SCOPES.map(({ value, label }) => (
                  <label
                    key={value}
                    className="flex items-center gap-2 cursor-pointer text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]"
                  >
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(value)}
                      onChange={() => toggleScope(value)}
                      className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
                    />
                    {label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2 text-sm text-[var(--coral)]">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Key
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// New Key Display (show raw key once)
// ============================================================================

interface NewKeyDisplayProps {
  result: NewKeyResult;
  onDismiss: () => void;
}

function NewKeyDisplay({ result, onDismiss }: NewKeyDisplayProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="border border-[var(--green)] bg-[var(--green-dim)] p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[var(--green-dim)]">
          <Check className="h-4 w-4 text-[var(--green)]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
            API key created — save it now
          </p>
          <p className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5">
            This key will not be shown again. Copy it somewhere safe.
          </p>
        </div>
      </div>

      <div className="border border-[var(--green)] bg-[var(--bg-raised)] px-3 py-2.5 font-mono text-sm">
        <div className="flex items-center justify-between gap-2">
          <span className={`flex-1 truncate ${revealed ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)] select-none'}`}>
            {revealed ? result.key || `pk_live_${result.key_prefix}...` : '••••••••••••••••••••••••••••••••••••••••'}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => setRevealed((v) => !v)}
              className="p-1.5 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] transition-colors"
              title={revealed ? 'Hide' : 'Reveal'}
            >
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            {result.key && <CopyButton text={result.key} label="Copy" />}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onDismiss}
          className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)] hover:text-[var(--green)] dark:hover:text-[var(--text-ter)]"
        >
          I've saved the key, dismiss
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Key Row
// ============================================================================

interface KeyRowProps {
  apiKey: ApiKey;
  onRevoke: (id: string) => void;
  revoking: boolean;
}

function KeyRow({ apiKey, onRevoke, revoking }: KeyRowProps) {
  const isRevoked = !!apiKey.revoked_at;
  const isExpired = apiKey.expires_at ? new Date(apiKey.expires_at) < new Date() : false;
  const inactive = isRevoked || isExpired;

  return (
    <div className={` border p-4 transition-colors ${inactive ? 'border-[var(--rule)] bg-[var(--surface)] opacity-60' : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--rule)]'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center  ${inactive ? 'bg-[var(--hover-tint)]' : 'bg-primary-50'}`}>
            <KeyRound className={`h-4 w-4 ${inactive ? 'text-[var(--text-ter)]' : 'text-primary-600'}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[var(--text-pri)]">{apiKey.name}</span>
              {isRevoked && (
                <span className="inline-flex items-center rounded-full bg-[var(--coral-dim)] px-2 py-0.5 text-xs font-medium text-[var(--coral)]">
                  Revoked
                </span>
              )}
              {isExpired && !isRevoked && (
                <span className="inline-flex items-center rounded-full bg-[var(--mustard-dim)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
                  Expired
                </span>
              )}
              {!inactive && (
                <span className="inline-flex items-center rounded-full bg-[var(--green-dim)] px-2 py-0.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                  Active
                </span>
              )}
              {apiKey.is_agent && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]"
                  title="Agent key — cannot perform authority transitions (ratify, publish, accept) regardless of scopes"
                >
                  <Shield className="h-3 w-3" />
                  Agent
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-[var(--text-ter)]">
              <code className="rounded bg-[var(--hover-tint)] px-1.5 py-0.5 font-mono text-[var(--text-sec)]">
                pk_live_{apiKey.key_prefix}…
              </code>
              <span>·</span>
              <span>Created {formatDate(apiKey.created_at)}</span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Last used: {relativeTime(apiKey.last_used_at)}
              </span>
            </div>
            {apiKey.scopes.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {apiKey.scopes.map((scope) => (
                  <span
                    key={scope}
                    className="inline-flex items-center rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs text-[var(--text-sec)]"
                  >
                    {scope}
                  </span>
                ))}
              </div>
            )}
            {apiKey.scopes.length === 0 && !inactive && (
              <div className="mt-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--mustard-dim)] border border-[var(--mustard)] px-2 py-0.5 text-xs text-[var(--mustard)]">
                  <Shield className="h-3 w-3" />
                  Full access
                </span>
              </div>
            )}
          </div>
        </div>

        {!inactive && (
          <button
            onClick={() => onRevoke(apiKey.id)}
            disabled={revoking}
            className="flex-shrink-0 inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] hover:text-[var(--coral)] transition-colors disabled:opacity-50"
          >
            {revoking ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Getting Started Steps
// ============================================================================

const STEPS = [
  {
    n: '1',
    title: 'Create an API key',
    body: 'Click "Create Key" above. Choose scopes, or leave blank for full access. The raw key is shown once — save it immediately.',
  },
  {
    n: '2',
    title: 'Note the base URL',
    body: 'All API requests go to the base URL shown below. Prefix every resource path with /v1/ (e.g. /v1/items).',
  },
  {
    n: '3',
    title: 'Authenticate requests',
    body: 'Pass your key as a Bearer token in every request: Authorization: Bearer pk_live_…',
  },
  {
    n: '4',
    title: 'Try it or connect an AI assistant',
    body: 'Hit /health to confirm the API is reachable, then query /v1/items — or skip manual requests entirely by connecting via the MCP server below.',
  },
];

function GettingStarted() {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-6">
      <h2 className="text-base font-semibold text-[var(--text-pri)] mb-4">Getting started</h2>
      <ol className="space-y-4">
        {STEPS.map((step) => (
          <li key={step.n} className="flex gap-4">
            <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
              {step.n}
            </span>
            <div className="pt-0.5">
              <p className="text-sm font-semibold text-[var(--text-pri)]">{step.title}</p>
              <p className="mt-0.5 text-sm text-[var(--text-ter)] leading-relaxed">{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ============================================================================
// API Docs Link
// ============================================================================

function ApiDocsLink() {
  return (
    <a
      href="/prioritization/documentation/api"
      className="flex items-center justify-between gap-4 border border-[var(--rule)] bg-[var(--bg-raised)] px-5 py-4 hover:border-primary-400 hover:shadow-sm transition-all group"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center bg-primary-50 group-hover:bg-primary-100 transition-colors">
          <BookOpen className="h-4 w-4 text-primary-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--text-pri)]">API Reference</p>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Full endpoint docs — items, sessions, cycles, catchball, reports, webhooks
          </p>
        </div>
      </div>
      <ChevronDown className="h-4 w-4 text-[var(--text-ter)] group-hover:text-primary-600 flex-shrink-0 transition-colors rotate-[-90deg]" />
    </a>
  );
}

// ============================================================================
// MCP Server Card
// ============================================================================

function McpCallout({ apiBaseUrl }: { apiBaseUrl: string }) {
  const [expanded, setExpanded] = useState(false);

  const mcpBaseUrl = apiBaseUrl.replace('/v1', '');

  const cursorConfig = `{
  "mcpServers": {
    "priorities-ai": {
      "command": "npx",
      "args": ["-y", "@priorities-ai/mcp-server"],
      "env": {
        "PRIORITIES_API_KEY": "pk_live_your_key_here",
        "PRIORITIES_BASE_URL": "${mcpBaseUrl}"
      }
    }
  }
}`;

  return (
    <div className="border-2 border-primary-200 bg-[var(--bg-raised)] overflow-hidden">
      {/* Header bar */}
      <div className="bg-primary-600 px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center bg-[var(--bg-raised)]/20">
            <Bot className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-base font-bold text-white">MCP Server</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-raised)]/20 px-2 py-0.5 text-xs font-semibold text-white">
                <Zap className="h-3 w-3" />
                Recommended
              </span>
            </div>
            <p className="text-sm text-primary-100 mt-0.5">
              Connect Claude or Cursor directly to this workspace — no code required
            </p>
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 bg-[var(--bg-raised)]/15 hover:bg-[var(--bg-raised)]/25 px-3 py-1.5 text-sm font-medium text-white transition-colors flex-shrink-0"
        >
          {expanded ? 'Hide setup' : 'Show setup'}
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* What you get */}
      <div className="px-6 py-5 border-b border-[var(--rule)]">
        <p className="text-sm text-[var(--text-sec)] leading-relaxed">
          The priorities.ai MCP server exposes <strong>35 tools</strong> to any AI assistant that
          supports the Model Context Protocol — including Claude Desktop and Cursor. Once connected,
          you can ask questions and take actions in plain language:
        </p>
        <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
          {[
            '"What are our top 10 priorities right now?"',
            '"Add this feature to the backlog"',
            '"Set up a RICE scoring session for Q2"',
            '"Which items are blocking the most work?"',
            '"Give me a health check on the current cycle"',
            '"Show me catchball proposals that need review"',
          ].map((q) => (
            <li key={q} className="flex items-start gap-2 text-sm text-[var(--text-sec)]">
              <span className="mt-0.5 text-primary-400 flex-shrink-0">›</span>
              <span className="italic">{q}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Setup instructions — expandable */}
      {expanded && (
        <div className="px-6 py-5 space-y-5 bg-[var(--surface)]">
          <div>
            <p className="text-sm font-semibold text-[var(--text-pri)] mb-1">
              Step 1 — Create an API key on this page
            </p>
            <p className="text-sm text-[var(--text-ter)]">
              For a read-only assistant, use scopes:{' '}
              <code className="rounded bg-[var(--hover-tint)] px-1.5 py-0.5 text-xs text-[var(--text-sec)]">
                items:read · sessions:read · cycles:read · catchball:read · reports:read
              </code>
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-[var(--text-pri)]">
                Step 2 — Add to your AI client config
              </p>
              <CopyButton text={cursorConfig} label="Copy" />
            </div>
            <p className="text-xs text-[var(--text-ter)] mb-2">
              <strong>Cursor:</strong>{' '}
              <code className="rounded bg-[var(--hover-tint)] px-1 py-0.5">.cursor/mcp.json</code>
              {'  ·  '}
              <strong>Claude Desktop:</strong>{' '}
              <code className="rounded bg-[var(--hover-tint)] px-1 py-0.5">
                ~/Library/Application Support/Claude/claude_desktop_config.json
              </code>
            </p>
            <pre className="bg-[var(--bg)] px-5 py-4 text-sm text-[var(--text-pri)] overflow-x-auto leading-relaxed font-mono">
              {cursorConfig}
            </pre>
          </div>

          <div className="border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
            <strong>Step 3</strong> — Replace{' '}
            <code className="rounded bg-primary-100 px-1">pk_live_your_key_here</code> with the key
            you just created, then restart your AI client. The 35 priorities.ai tools will appear
            automatically.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState<NewKeyResult | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const workspaceId = getCurrentWorkspaceId() ?? '';

  const loadKeys = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);

    const supabase = getSupabaseClient();
    const { data, error: dbErr } = await supabase
      .from('api_keys')
      .select('id, name, key_prefix, scopes, is_agent, last_used_at, expires_at, created_at, revoked_at, created_by')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (dbErr) {
      setError(dbErr.message);
    } else {
      setKeys(data ?? []);
    }
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreated = (result: NewKeyResult) => {
    setShowCreateDialog(false);
    setNewKeyResult(result);
    loadKeys();
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Revoke this API key? Any integrations using it will stop working immediately.')) return;
    setRevokingId(id);
    const supabase = getSupabaseClient();
    await supabase
      .from('api_keys')
      .update({ revoked_at: new Date().toISOString() })
      .eq('id', id)
      .eq('workspace_id', workspaceId);
    setRevokingId(null);
    loadKeys();
  };

  const activeKeys = keys.filter((k) => !k.revoked_at && (!k.expires_at || new Date(k.expires_at) >= new Date()));
  const inactiveKeys = keys.filter((k) => k.revoked_at || (k.expires_at && new Date(k.expires_at) < new Date()));

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const apiBaseUrl = supabaseUrl ? `${supabaseUrl}/functions/v1/api/v1` : '…/functions/v1/api/v1';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<KeyRound className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="API Keys"
        subtitle="Manage API keys for programmatic access to this workspace"
        actions={
          <button
            onClick={() => setShowCreateDialog(true)}
            className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Key
          </button>
        }
      />

      {/* Getting started */}
      <GettingStarted />

      {/* MCP Server — featured */}
      <McpCallout apiBaseUrl={apiBaseUrl} />

      {/* API docs link */}
      <ApiDocsLink />

      {/* Base URL reference */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
        <p className="text-xs font-medium text-[var(--text-ter)] mb-1">API base URL</p>
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono text-[var(--text-pri)] select-all">{apiBaseUrl}</code>
          <CopyButton text={apiBaseUrl} label="Copy" />
        </div>
        <p className="mt-2 text-xs text-[var(--text-ter)]">
          Add <code className="bg-[var(--bg-raised)] rounded px-1">Authorization: Bearer &lt;key&gt;</code> to every request.
        </p>
      </div>

      {/* Newly created key banner */}
      {newKeyResult && (
        <NewKeyDisplay
          result={newKeyResult}
          onDismiss={() => setNewKeyResult(null)}
        />
      )}

      {/* Key list */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 text-sm text-[var(--coral)]">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      ) : keys.length === 0 ? (
        <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] px-6 py-12 text-center">
          <KeyRound className="mx-auto h-8 w-8 text-[var(--text-ter)] mb-3" />
          <p className="text-sm font-medium text-[var(--text-ter)]">No API keys yet</p>
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Create a key to enable programmatic access to this workspace.
          </p>
          <button
            onClick={() => setShowCreateDialog(true)}
            className="mt-4 inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create your first key
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {activeKeys.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-[var(--text-sec)]">
                Active ({activeKeys.length})
              </h3>
              <div className="space-y-2">
                {activeKeys.map((key) => (
                  <KeyRow
                    key={key.id}
                    apiKey={key}
                    onRevoke={handleRevoke}
                    revoking={revokingId === key.id}
                  />
                ))}
              </div>
            </div>
          )}

          {inactiveKeys.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-[var(--text-ter)]">
                Revoked / Expired ({inactiveKeys.length})
              </h3>
              <div className="space-y-2">
                {inactiveKeys.map((key) => (
                  <KeyRow
                    key={key.id}
                    apiKey={key}
                    onRevoke={handleRevoke}
                    revoking={revokingId === key.id}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {showCreateDialog && workspaceId && (
        <CreateKeyDialog
          workspaceId={workspaceId}
          onClose={() => setShowCreateDialog(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}
